import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Loader2 } from "lucide-react";

interface ChartDataPoint {
  month: string;
  value: number;
}

interface ChartConfig {
  title: string;
  data: ChartDataPoint[];
  color: string;
  fillId: string;
}

interface MonthlyEvolutionChartsProps {
  charts: ChartConfig[];
  isLoading: boolean;
}

export default function MonthlyEvolutionCharts({ charts, isLoading }: MonthlyEvolutionChartsProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8"
    >
      {charts.map((chart, i) => (
        <div key={i} className="bg-card rounded-2xl p-5 border border-border">
          <h3 className="font-heading text-sm font-semibold text-foreground mb-4">{chart.title}</h3>
          <div className="h-40">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart.data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id={chart.fillId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chart.color} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={chart.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    labelStyle={{ color: "hsl(var(--foreground))" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={chart.color}
                    strokeWidth={2}
                    fill={`url(#${chart.fillId})`}
                    name="Total"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      ))}
    </motion.div>
  );
}
